/** @jsxRuntime classic */
/** @jsx jsx */

import keycode from 'keycode';
import { useState, useEffect } from 'react';
import { jsx, css } from '@emotion/react';
import {
  FloatingBox,
  IconButton,
  Menu,
  Colors,
  useClickOutside,
} from '@zapier/design-system';
import { labelStyles } from '@zapier/style-encapsulation';
import { Link } from 'components/Link/Link';

import { CollapsibleMenu } from './CollapsibleMenu';
import type { NavItemType, NavSize, OnChooseItem } from './types';
import styled from '@emotion/styled';

type Props = {
  /**
   * Whether to render a selected state.
   */
  shouldIndicateSelected: boolean;
  /**
   * Array of items to render in the dropdown.
   */
  items: Array<NavItemType>;
  /**
   * Callback for when an item is chosen.
   */
  onChooseItem: OnChooseItem;
  /**
   * Indicates which item is selected based on its href.
   */
  selectedItemHref: string;
  /**
   * The size to render the dropdown at.
   */
  size: NavSize;
};

const Styles = labelStyles('NavDropdown', {
  root: {
    position: 'relative',
  },

  summary: css`
    // Hides default icon on details element
    &::-webkit-details-marker {
      display: none;
    }

    &::marker {
      content: '';
    }

    &::before {
      display: none;
    }
  `,

  menu: [
    css`
      padding: 20px 10px;
      min-width: 250px;
    `,
  ],
});

/**
 * This is what renders the dropdown/menu when you click the three ... button when the user is on a smaller screen.
 * This component exists in ZDS, but, doesn't have the capability of displaying submenus. That said, we had to add that functionality
 * on top of the existing code.
 */
export const NavDropdown = (props: Props) => {
  const [isOpen, setIsOpen] = useState(false);
  const [revealedMenuItemIndex, setRevealedMenuItemIndex] = useState<
    null | number
  >(null);

  useEffect(() => {
    if (!isOpen && revealedMenuItemIndex !== null) {
      setRevealedMenuItemIndex(null);
    }
  }, [isOpen]);

  const menuItems = props.items.map((item, index) => {
    /**
     * Does the item contain a submenu or not?
     */
    if (!item.submenu.length) {
      return (
        <li key={index}>
          <LinkWrapper>
            <Link
              onClick={(event) => {
                /**
                 * We don't want the event to propagate up, because the parent
                 * `details` element will cancel the default click event.
                 */
                event.stopPropagation();
                setIsOpen(false);
                props.onChooseItem(item, event);
              }}
              href={item.href}
            >
              <LinkTitle>{item.text}</LinkTitle>
            </Link>
          </LinkWrapper>
        </li>
      );
    }
    const isMenuItemOpen = revealedMenuItemIndex === index;
    return (
      <li key={index}>
        <CollapsibleMenu
          title={item.text}
          submenu={item.submenu}
          onClick={() =>
            isMenuItemOpen
              ? setRevealedMenuItemIndex(null)
              : setRevealedMenuItemIndex(index)
          }
          isOpen={isMenuItemOpen}
          onChildClick={(event) => {
            setIsOpen(false);
            props.onChooseItem(item, event);
          }}
        />
      </li>
    );
  });
  const [setClickOutsideNode] = useClickOutside({
    onClickOutside: () => {
      setIsOpen(false);
    },
  });
  // Define handlers here to avoid non-applicable linter a11y complaints.
  const detailsHandlers = {
    onClick: (event: React.MouseEvent) => event.preventDefault(),
    onKeyUp: (event: React.KeyboardEvent) => {
      if (event.keyCode === keycode('escape')) {
        setIsOpen(false);
      }
    },
  };
  // Define handlers here to avoid non-applicable linter a11y complaints.
  const summaryHandlers = {
    onClick: () => setIsOpen(!isOpen),
  };
  return (
    <details
      {...detailsHandlers}
      css={Styles.root}
      open={isOpen}
      ref={setClickOutsideNode}
    >
      <summary
        {...summaryHandlers}
        aria-expanded={isOpen}
        aria-label="More items…"
        css={Styles.summary}
        role="button"
        tabIndex={0}
      >
        <IconButton
          icon="navMoreHoriz"
          label="More items…"
          size={
            {
              medium: 'xsmall' as 'xsmall',
              large: 'small' as 'small',
            }[props.size]
          }
          tooltipPosition="west"
        />
      </summary>
      {isOpen && (
        <FloatingBox align="right">
          <div css={Styles.menu}>
            <Menu ariaLabel="">{menuItems}</Menu>
          </div>
        </FloatingBox>
      )}
    </details>
  );
};

const LinkWrapper = styled.div`
  display: block;
  border-radius: 3px;
  color: ${Colors.neutral700};
  line-height: 20px;
  font-size: 14px;
  font-weight: 500;

  &:hover,
  &:focus {
    color: ${Colors.blueJeans};
    background-color: ${Colors.acidWash};
  }
`;

const LinkTitle = styled.p`
  padding: 10px;
`;
